/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, ReactNode, Ref, RefAttributes } from 'react';

import {
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormGroup,
  FormHelperText,
  FormLabel,
  Stack,
  useTheme,
} from '@mui/material';
import {
  Control,
  FieldError,
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';

import { useFormError } from './FormErrorProvider';
import { IOptions } from '../interfaces/IOptions';

export type CheckboxButtonGroupProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  options: IOptions[] | any[];
  helperText?: ReactNode;
  name: TName;
  required?: boolean;
  parseError?: (error: FieldError) => ReactNode;
  label?: string;
  labelKey?: string;
  valueKey?: string;
  onChange?: (data: IOptions[] | (string | number)[]) => void;
  returnObject?: boolean;
  disabled?: boolean;
  row?: boolean;
  control?: Control<TFieldValues>;
  rules?: UseControllerProps<TFieldValues, TName>['rules'];
  checkboxColor?: CheckboxProps['color'];
  labelProps?: Omit<FormControlLabelProps, 'label' | 'control'>;
  renderAction?: (id: string) => React.ReactNode;
};

type CheckboxButtonGroupComponent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: CheckboxButtonGroupProps<TFieldValues, TName> &
    RefAttributes<HTMLDivElement>
) => JSX.Element;

const CheckboxButtonGroup = forwardRef(function CheckboxButtonGroup<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: CheckboxButtonGroupProps<TFieldValues, TName>,
  ref: Ref<HTMLDivElement>
): JSX.Element {
  const {
    helperText,
    options,
    label,
    name,
    parseError,
    required,
    labelKey = 'name',
    valueKey = 'id',
    returnObject,
    disabled,
    row,
    control,
    checkboxColor,
    rules,
    labelProps,
    renderAction,
    ...rest
  } = props;

  const theme = useTheme();
  const errorMsgFn = useFormError();
  const customErrorFn = parseError || errorMsgFn;

  const {
    field: { value = [], onChange },
    fieldState: { error },
  } = useController({
    name,
    rules: required ? { required: 'This field is required' } : rules,
    disabled,
    control,
  });

  const renderHelperText = error
    ? typeof customErrorFn === 'function'
      ? customErrorFn(error)
      : error.message
    : helperText;

  const handleChange = (index: number | string) => {
    const newArray: (string | number)[] | IOptions[] = [...value];
    const exists =
      value.findIndex((i: any) =>
        returnObject ? i[valueKey] === index : i === index
      ) === -1;
    if (exists) {
      newArray.push(
        returnObject ? options.find((i) => i[valueKey] === index) : index
      );
    } else {
      newArray.splice(
        value.findIndex((i: any) =>
          returnObject ? i[valueKey] === index : i === index
        ),
        1
      );
    }
    onChange(newArray);
    if (typeof rest.onChange === 'function') {
      rest.onChange(newArray);
    }
  };

  return (
    <FormControl error={!!error} required={required} ref={ref}>
      {label && <FormLabel error={!!error}>{label}</FormLabel>}
      <FormGroup row={row}>
        {options.map((option: any, index: number) => {
          const optionKey = option[valueKey];
          if (!optionKey) {
            console.error(
              `CheckboxButtonGroup: valueKey ${valueKey} does not exist on option`,
              option
            );
          }
          const isChecked =
            value.findIndex((item: any) =>
              returnObject ? item[valueKey] === optionKey : item === optionKey
            ) !== -1;
          return (
            <Stack
              direction="row"
              justifyContent="space-between"
              key={`${option[labelKey]}-${index}`}
            >
              <FormControlLabel
                {...labelProps}
                control={
                  <Checkbox
                    sx={{
                      color: error ? theme.palette.error.main : undefined,
                    }}
                    color={checkboxColor || 'primary'}
                    value={optionKey}
                    checked={isChecked}
                    disabled={disabled}
                    onChange={() => handleChange(optionKey)}
                  />
                }
                label={option[labelKey]}
                key={optionKey}
              />
              {renderAction?.(option.id)}
            </Stack>
          );
        })}
      </FormGroup>
      {renderHelperText && <FormHelperText>{renderHelperText}</FormHelperText>}
    </FormControl>
  );
});

CheckboxButtonGroup.displayName = 'CheckboxButtonGroup';
export default CheckboxButtonGroup as CheckboxButtonGroupComponent;
